
import { DOCUMENT_API_V1 } from "./apiBase";

const API = DOCUMENT_API_V1;


async function apiRequest(path, options = {}) {
  const url = `${API}${path}`;

  let response;

  try {
    response = await fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",

        // Bypass ngrok free-plan warning
        "ngrok-skip-browser-warning": "true",

        // Preserve existing request headers
        ...options.headers,
      },
    });
  } catch (error) {
    throw new Error(
      `Network error: ${error.message}`
    );
  }

  const raw = await response.text();

  const contentType =
    response.headers.get("content-type") || "";

  // Detect ngrok warning responses
  const ngrokError =
    response.headers.get("ngrok-error-code");

  if (ngrokError) {
    throw new Error(
      `Ngrok error (${ngrokError}). The request did not reach the API.`
    );
  }

  let body = null;

  try {
    body = raw ? JSON.parse(raw) : null;
  } catch {
    body = raw;
  }

  if (!response.ok) {
    const detail =
      body?.detail ??
      body?.message ??
      body?.error;

    throw new Error(
      typeof detail === "string"
        ? detail
        : detail != null
          ? JSON.stringify(detail)
          : `HTTP ${response.status}: ${raw.slice(0, 200)}`
    );
  }

  if (
    typeof body === "string" &&
    (
      contentType.includes("text/html") ||
      contentType.includes("text/plain") ||
      /^\s*</.test(body)
    )
  ) {
    throw new Error(
      "The server returned text or HTML instead of JSON. Check the ngrok tunnel and API."
    );
  }

  return body;
}


function normalizeList(payload, entity) {
  if (Array.isArray(payload)) return payload;

  const keys = [
    entity,
    "items",
    "results",
    "records",
    "data",
    "list",
  ];

  function search(value, depth = 0) {
    if (Array.isArray(value)) return value;

    if (
      value === null ||
      typeof value !== "object" ||
      depth > 5
    ) {
      return null;
    }

    for (const key of keys) {
      if (Array.isArray(value[key])) {
        return value[key];
      }
    }

    for (const key of [
      ...keys,
      "response",
      "result",
      "payload",
    ]) {
      if (value[key] != null) {
        const found = search(
          value[key],
          depth + 1
        );

        if (found !== null) return found;
      }
    }

    return null;
  }

  const result = search(payload);

  if (result !== null) return result;

  throw new Error(
    `Unrecognized ${entity} response: ${JSON.stringify(payload).slice(0, 350)}`
  );
}

async function fetchAllPages(path, entity) {
  const all = [];
  const limit = 500;

  for (let skip = 0; skip < 10000; skip += limit) {
    const separator = path.includes("?")
      ? "&"
      : "?";

    const result = await apiRequest(
      `${path}${separator}skip=${skip}&limit=${limit}`
    );

    const page = normalizeList(result, entity);
    all.push(...page);

    if (page.length < limit) break;
  }

  return all;
}

// GET /api/v1/projects
export async function listProjects() {
  return fetchAllPages("/projects", "projects");
}

// POST /api/v1/projects
export async function createProject({
  name,
  description = "",
}) {
  const trimmedName = String(name || "").trim();

  if (!trimmedName) {
    throw new Error("Project name is required.");
  }

  const result = await apiRequest("/projects", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: trimmedName,
      description: String(description || "").trim(),
    }),
  });

  const project =
    result?.project ?? result?.data ?? result;

  const id = project?.id ?? project?.project_id;

  if (!id) {
    throw new Error(
      "Project creation returned no project ID. Check the API response before retrying."
    );
  }

  return project;
}

// GET /api/v1/projects/{project_id}/documents
export async function listDocuments(projectId) {
  if (!projectId) {
    throw new Error("Project ID is required.");
  }

  return fetchAllPages(
    `/projects/${encodeURIComponent(projectId)}/documents`,
    "documents"
  );
}

// POST /api/v1/projects/{project_id}/documents
export async function uploadProjectDocuments({
  projectId,
  files,
  documentType = "",
  source = "plan",
  vendorId = "",
}) {
  if (!projectId) {
    throw new Error("Select a project.");
  }

  if (!files?.length) {
    throw new Error("Choose files to upload.");
  }

  const allowed =
    /\.(xlsx|xls|xlsm|csv|tsv|pdf|docx|png|jpg|jpeg|tiff)$/i;

  const form = new FormData();

  for (const file of files) {
    if (!allowed.test(file.name)) {
      throw new Error(
        `Unsupported file: ${file.name}`
      );
    }

    if (file.size > 50 * 1024 * 1024) {
      throw new Error(
        `${file.name} exceeds 50 MB.`
      );
    }

    form.append("files", file);
  }

  if (documentType.trim()) {
    form.append(
      "document_type",
      documentType.trim()
    );
  }

  form.append("source", source);

  if (source === "vendor") {
    if (!vendorId.trim()) {
      throw new Error(
        "Vendor ID is required."
      );
    }

    form.append("vendor_id", vendorId.trim());
  }

  return apiRequest(
    `/projects/${encodeURIComponent(projectId)}/documents`,
    {
      method: "POST",
      body: form,
    }
  );
}

// POST /api/v1/documents/{document_id}/extract-ai
export async function extractDocumentAI(documentId) {
  if (!documentId) {
    throw new Error("Document ID is required.");
  }

  return apiRequest(
    `/documents/${encodeURIComponent(documentId)}/extract-ai?sync=false`,
    {
      method: "POST",
    }
  );
}
